import React from 'react';
import LegalLayout, { LegalH2, LegalH3, LegalP, LegalUl, LegalTable } from './LegalLayout';

/* Re-opens the cookie banner so the user can change their mind */
const resetConsent = () => {
  try {
    localStorage.removeItem('akashica_tracking_consent');
    window.location.reload();
  } catch (e) {
    /* noop */
  }
};

const Cookies = () => (
  <LegalLayout
    title="Política de cookies"
    subtitle="Qué cookies y tecnologías similares usamos, para qué, y cómo puedes gestionarlas."
    lastUpdate="Abril 2026"
  >
    <LegalH2 id="que-son">1. Qué son las cookies</LegalH2>
    <LegalP>
      Las cookies son pequeños archivos que se descargan en tu dispositivo al visitar un sitio web. Junto con otras tecnologías similares (almacenamiento local, identificadores asignados en el navegador), permiten que el Sitio funcione correctamente, recuerde tus preferencias o mida cómo se usa.
    </LegalP>
    <LegalP>
      En nuestro caso usamos el almacenamiento local del navegador para funciones técnicas (por ejemplo, mantener tu sesión) y para analítica propia. A efectos de esta política, llamamos "cookies" también a estos identificadores locales, tal y como recomienda la Agencia Española de Protección de Datos.
    </LegalP>

    <LegalH2 id="usa">2. Qué cookies usamos</LegalH2>
    <LegalP>
      Esta es la lista detallada de cookies y tecnologías similares que puede instalar nuestro Sitio, ordenadas por categoría:
    </LegalP>

    <LegalH3>2.1 Cookies técnicas (exentas del deber de consentimiento)</LegalH3>
    <LegalTable
      headers={['Identificador', 'Tipo', 'Titular', 'Finalidad', 'Duración']}
      rows={[
        ['token', 'Almacenamiento local', 'Propia', 'Mantener iniciada tu sesión como alumna', 'Hasta cierre de sesión o expiración del token'],
        ['akashica_tracking_consent', 'Almacenamiento local', 'Propia', 'Recordar tu elección sobre analítica para no volver a preguntarte', '12 meses'],
      ]}
    />

    <LegalH3>2.2 Cookies de analítica propia (requieren consentimiento)</LegalH3>
    <LegalTable
      headers={['Identificador', 'Tipo', 'Titular', 'Finalidad', 'Duración']}
      rows={[
        ['akashica_visitor_id', 'Almacenamiento local', 'Propia', 'Medir de forma agregada cómo se usa el Sitio (páginas vistas, origen, conversiones) con un identificador que no se cruza con ningún otro dato personal fuera del propio Sitio', '12 meses'],
      ]}
    />

    <LegalH3>2.3 Cookies de terceros que pueden activarse por servicios concretos</LegalH3>
    <LegalTable
      headers={['Proveedor', 'Cuándo se activa', 'Finalidad', 'Más información']}
      rows={[
        [
          'PayPal',
          'Al usar PayPal para pagar una formación o iniciar la suscripción al Campus',
          'Procesar el pago de forma segura y prevenir el fraude',
          <a key="pp" href="https://www.paypal.com/es/webapps/mpp/ua/cookie-full" target="_blank" rel="noopener noreferrer" className="text-[#c9a227] hover:underline">Política de cookies de PayPal</a>
        ],
        [
          'Instagram / WhatsApp (Meta)',
          'Solo si haces clic en un enlace a estas redes',
          'Gestión propia de cada plataforma',
          'Las gestiona Meta, no este Sitio'
        ],
      ]}
    />
    <LegalP>
      No utilizamos Google Analytics, Meta Pixel, TikTok Pixel ni herramientas equivalentes de publicidad o perfilado.
    </LegalP>

    <LegalH2 id="consentimiento">3. Cómo obtenemos tu consentimiento</LegalH2>
    <LegalP>
      La primera vez que accedes al Sitio se muestra un panel informativo donde puedes:
    </LegalP>
    <LegalUl>
      <li><strong>Aceptar</strong> el uso de cookies no esenciales (analítica propia).</li>
      <li><strong>Rechazar</strong> todas las cookies no esenciales. Las cookies técnicas siempre permanecen porque son necesarias para que el Sitio funcione.</li>
    </LegalUl>
    <LegalP>
      Hasta que nos indiques tu preferencia no se instalan cookies analíticas. Guardamos tu elección para no volver a preguntarte en cada visita, pero puedes cambiarla cuando quieras desde el botón siguiente.
    </LegalP>

    <div className="my-6">
      <button
        onClick={resetConsent}
        className="px-5 py-3 rounded-full bg-[#c9a227] text-white font-sans font-medium hover:bg-[#b89220] transition-colors text-sm"
        data-testid="reopen-cookie-banner"
      >
        Cambiar mis preferencias de cookies
      </button>
    </div>

    <LegalH2 id="revocar">4. Cómo revocar o bloquear cookies desde tu navegador</LegalH2>
    <LegalP>
      Además del botón anterior, puedes gestionar o bloquear las cookies desde la configuración de tu navegador:
    </LegalP>
    <LegalUl>
      <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-[#c9a227] hover:underline">Google Chrome</a></li>
      <li><a href="https://support.mozilla.org/es/kb/habilitar-y-deshabilitar-cookies-sitios-web-rastrear-preferencias" target="_blank" rel="noopener noreferrer" className="text-[#c9a227] hover:underline">Mozilla Firefox</a></li>
      <li><a href="https://support.apple.com/es-es/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-[#c9a227] hover:underline">Safari</a></li>
      <li><a href="https://support.microsoft.com/es-es/windows/eliminar-y-administrar-cookies-168dab11-0753-043d-7c16-ede5947fc64d" target="_blank" rel="noopener noreferrer" className="text-[#c9a227] hover:underline">Microsoft Edge</a></li>
    </LegalUl>
    <LegalP>
      Ten en cuenta que si bloqueas todas las cookies, algunas funciones del Sitio pueden no comportarse como esperas (por ejemplo, iniciar sesión).
    </LegalP>

    <LegalH2 id="cambios">5. Cambios en esta política</LegalH2>
    <LegalP>
      Revisamos esta política cuando actualizamos el Sitio o añadimos servicios. Publicaremos siempre la versión vigente en esta misma página, con la fecha de última actualización.
    </LegalP>
  </LegalLayout>
);

export default Cookies;
